import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AuthApiError, getAuthClient } from '@/api/client';
import type { JoinRequest, UserSiteMembership } from '@/api/types';
import { errorMessage } from '@/lib/errorMap';

const STATUS_LABEL: Record<string, string> = {
  pending: '검토 중',
  approved: '승인됨',
  rejected: '거절',
  cancelled: '취소됨',
};

function statusVariant(s: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'approved') return 'default';
  if (s === 'pending') return 'secondary';
  if (s === 'rejected') return 'destructive';
  return 'outline';
}

export function MyRequestsPage() {
  const [orgRequests, setOrgRequests] = useState<JoinRequest[]>([]);
  const [siteRequests, setSiteRequests] = useState<UserSiteMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [orgs, sites] = await Promise.all([
        getAuthClient().myJoinRequests(),
        getAuthClient().listMySiteMemberships(),
      ]);
      setOrgRequests(orgs);
      setSiteRequests(sites);
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onCancel = async (id: string) => {
    try {
      await getAuthClient().cancelJoinRequest(id);
      toast.success('요청이 취소됐습니다');
      await refresh();
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      else toast.error('네트워크 오류');
    }
  };

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">내 가입 요청</h1>
          <Button asChild size="sm">
            <Link to="/find-org">조직 찾기</Link>
          </Button>
        </div>

        <Tabs defaultValue="org">
          <TabsList>
            <TabsTrigger value="org">조직 가입</TabsTrigger>
            <TabsTrigger value="site">사이트 가입</TabsTrigger>
          </TabsList>

          <TabsContent value="org" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">조직 가입 요청 ({orgRequests.length}건)</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-sm text-muted-foreground">불러오는 중...</div>
                ) : orgRequests.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    아직 요청한 조직이 없습니다.
                  </div>
                ) : (
                  <ul className="flex flex-col divide-y">
                    {orgRequests.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-start justify-between gap-3 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge variant={statusVariant(r.status)}>
                              {STATUS_LABEL[r.status] ?? r.status}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(r.requested_at).toLocaleString('ko-KR')}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-baseline gap-2 text-sm">
                            <span className="font-medium">
                              {r.organization_name ?? '(이름 미상)'}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground break-all">
                              {r.organization_id}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              · 역할: {r.requested_role === 'org_admin' ? '관리자' : '일반 멤버'}
                            </span>
                          </div>
                          {r.message && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              메시지: {r.message}
                            </div>
                          )}
                          {r.rejection_reason && (
                            <div className="mt-1 text-xs text-destructive">
                              거절 사유: {r.rejection_reason}
                            </div>
                          )}
                        </div>
                        {r.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onCancel(r.id)}
                          >
                            취소
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="site" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  사이트 가입 요청 ({siteRequests.length}건)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-sm text-muted-foreground">불러오는 중...</div>
                ) : siteRequests.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    사이트 가입 요청이 없습니다.
                  </div>
                ) : (
                  <ul className="flex flex-col divide-y">
                    {siteRequests.map((m) => (
                      <li key={m.id} className="py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant={statusVariant(m.status)}>
                            {STATUS_LABEL[m.status] ?? m.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(m.requested_at).toLocaleString('ko-KR')}
                          </span>
                        </div>
                        <div className="mt-1 text-sm">
                          {m.site_name ?? `사이트 ${m.site_id.slice(0, 8)}`}
                        </div>
                        {m.rejection_reason && (
                          <div className="mt-1 text-xs text-destructive">
                            거절 사유: {m.rejection_reason}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
